import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import Logo from '../images/school_logo.png';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import 'jspdf-autotable';
import { useUserAuth } from "../context/UserAuthContext";
import {SCHOOL_NAME, SCHOOL_ACRONYM, SCHOOL_ADDRESS, SCHOOL_EMAIL, SCHOOL_PHONE, RESULT_COLOR_1, RESULT_COLOR_2, RESULT_COLOR_3 } from '../Variables.js';

const ClassResults = () => {
  const { user, userData } = useUserAuth();
  const { sessionId, termId, classId } = useParams();
  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionName, setSessionName] = useState('');
  const [termName, setTermName] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [formMasterName, setFormMasterName] = useState('');


  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchAll = async () => {
      try {
        // Fetch class data
        const classRef = doc(db, 'classes', classId);
        const classSnap = await getDoc(classRef);
        const classData = classSnap.data();
        setClassInfo(classData);
  
        // Fetch session name
        const sessionRef = doc(db, 'sessions', sessionId);
        const sessionSnap = await getDoc(sessionRef);
        setSessionName(sessionSnap.exists() ? sessionSnap.data().name : sessionId);
  
        // Fetch term name
        const termRef = doc(db, 'terms', termId);
        const termSnap = await getDoc(termRef);
        setTermName(termSnap.exists() ? termSnap.data().name : termId);
  
        // Fetch all subjects
        const allSubjectsSnap = await getDocs(collection(db, 'subjects'));
        const allSubjects = allSubjectsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
        // Fetch students
        const allStudentsSnap = await getDocs(collection(db, 'students'));
        const allStudents = allStudentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
        const filtered = allStudents.filter(student =>
          (student.enrollments || []).some(enr => enr.classId === classId)
        );
        setStudents(filtered);
  
        // Fetch all teachers
        const teachersSnap = await getDocs(collection(db, 'users'));
        const allTeachers = teachersSnap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(user => ['Teacher', 'Cashier', 'Admin'].includes(user.status) && user.isActive === true);
        setTeachers(allTeachers);
  
        // Get form master's name
        let formMasterName = '';
        if (classData.formMaster) {
          const formMasterDoc = await getDoc(doc(db, 'users', classData.formMaster));
          if (formMasterDoc.exists()) {
            const data = formMasterDoc.data();
            formMasterName = `${data.firstName} ${data.lastName}`;
          }                    
        }
        setFormMasterName(formMasterName); // ⬅️ Make sure this state is declared with useState
  
        // Map subject data to class subjects with teacher names
        const subjects = (classData.subjects || []).map(sub => {
          const full = allSubjects.find(s => s.id === sub.id);
          const assignedTeachers = allTeachers.filter(teacher =>
            teacher.teachingAssignments?.some(assignment =>
              assignment.classId === classId && assignment.subjectId === sub.id
            )
          );
  
          return {
            ...sub,
            name: full?.name || sub.id,
            assignedTeachers: assignedTeachers.map(t => `${t.firstName} ${t.lastName}`).join(', ')
          };
        });
        setSubjects(subjects);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchAll();
  }, [classId, sessionId, termId]);
  
  const getScore = (student, subjectId, type) => {
    const enrollment = (student.enrollments || []).find(enr => enr.classId === classId);
    if (!enrollment) return '-';

    const subj = (enrollment.subjects || []).find(sub => sub.subjectId === subjectId);
    return subj?.scores?.[type] ?? '-';
  };

  const getTotal = (student, subjectId) => {
    const first = parseFloat(getScore(student, subjectId, 'firstca')) || 0;
    const second = parseFloat(getScore(student, subjectId, 'secondca')) || 0;
    const exam = parseFloat(getScore(student, subjectId, 'exam')) || 0;
    return first + second + exam;
  };

  const getGrade = (total) => {
    if (total >= 70) return 'A';
    if (total >= 60) return 'B';
    if (total >= 50) return 'C';
    if (total >= 45) return 'D';
    if (total >= 40) return 'E';
    return 'F';
  };

  const getComment = (total) => {
    if (total >= 70) return 'An excellent result. Keep it up!';
    if (total >= 60) return 'A very good result. Try harder next term.';
    if (total >= 50) return 'A good result. You can do better.';
    if (total >= 45) return 'A fair result. Do better on your weak subjects.';
    if (total >= 40) return 'A fair result. Improve on your studies and focus in class.';
    return 'You can do much better. Seek help from your teachers or parents regularly.';
  };

  const getStudentName = (student) =>
    `${student.firstName} ${student.lastName} ${student.middleName || ''}`;

  // Add this function to calculate all student statistics
  const calculateStudentStats = (students, subjects) => {
    return students.map(student => {
      let grandTotal = 0;
      let subjectCount = 0;
      const subjectResults = {};

      subjects.forEach(subject => {
        const firstCA = parseFloat(getScore(student, subject.id, 'firstca')) || 0;
        const secondCA = parseFloat(getScore(student, subject.id, 'secondca')) || 0;
        const exam = parseFloat(getScore(student, subject.id, 'exam')) || 0;
        const total = firstCA + secondCA + exam;
        
        subjectResults[subject.id] = {
          firstCA,
          secondCA,
          exam,
          total,
          grade: getGrade(total)
        };

        grandTotal += total;
        subjectCount++;
      });

      const average = subjectCount > 0 ? grandTotal / subjectCount : 0;
      return {
        ...student,
        subjectResults,
        grandTotal,
        average: parseFloat(average.toFixed(2)),
        grandGrade: getGrade(average)
      };
    });
  };

  // Add this function to calculate class statistics
  const calculateClassStats = (studentsWithStats) => {
    const averages = studentsWithStats.map(s => s.average);
    const sortedStudents = [...studentsWithStats].sort((a, b) => b.average - a.average);
    
    // Assign positions
    sortedStudents.forEach((student, index) => {
      student.position = index + 1;
    });

    return {
      highestAverage: Math.max(...averages),
      lowestAverage: Math.min(...averages),
      classAverage: parseFloat((averages.reduce((a, b) => a + b, 0) / averages.length).toFixed(2)),
      topStudents: sortedStudents.slice(0, 3),
      bottomStudents: sortedStudents.slice(-3).reverse()
    };
  };

  const getPsychomotorSkills = (student) => {
    const enrollment = (student.enrollments || []).find(enr => enr.classId === classId);
    return enrollment?.psychomotorSkills || {};
  };

  const generatePDF = () => {
    if (!classInfo || students.length === 0) return;
  
    const doc = new jsPDF();
  
    subjects.forEach((subject, subjectIndex) => {
      if (subjectIndex > 0) doc.addPage();
  
      // === Add Logo ===
      const logoX = 14;
      const logoY = 10;
      const logoWidth = 20;
      const logoHeight = 20;
  
      doc.addImage(Logo, 'PNG', logoX, logoY, logoWidth, logoHeight); 
  
      // === School Name Next to Logo ===
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${SCHOOL_NAME}`.toUpperCase(), logoX + logoWidth + 5, logoY + 9);
  
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`${SCHOOL_ADDRESS}. ${SCHOOL_PHONE}`, logoX + logoWidth + 5, logoY + 15);
  
      // === Title & Meta Info ===
      const title = `${classInfo.name} - ${subject.name || 'Unnamed Subject'} Score Sheet`;
      const meta = `Session: ${sessionName} | Term: ${termName} | Students: ${students.length}`;
      const teacherInfo = `Teacher(s): ${subject.assignedTeachers || 'Not assigned'}`;
  
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 14, logoY + logoHeight + 10);
      doc.setFont('helvetica', 'normal');
      doc.text(meta, 14, logoY + logoHeight + 18);
      doc.text(teacherInfo, 14, logoY + logoHeight + 26);
  
      // === Table Data - with blank cells for scores ===
      const tableData = students.map((student, idx) => ([
        idx + 1,
        getStudentName(student),
        '',
        '',
        '',
        '',
        '',
        ''  
      ]));
  
      autoTable(doc, {
        startY: logoY + logoHeight + 35,
        head: [['S/N', 'Student Name', '1st CA', '2nd CA', 'Exam', 'Total', 'Grade', 'Remark']],
        body: tableData,
        styles: {
          fontSize: 10,
          cellPadding: 3,
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        alternateRowStyles: {
          fillColor: false
        },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1,
      });
    });
  
    doc.save(`${classInfo.name}_${sessionName}_${termName}_ScoreSheets.pdf`);
  };

  const generateBroadSheet = () => {
    if (!classInfo || students.length === 0 || subjects.length === 0) return;
  
    // PDF Setup
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm'
    });
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const headerHeight = 30;
    
    // Fixed to 4 subjects per page as requested
    const maxSubjectsPerPage = 4;
    
    // Calculate how many students fit vertically per page
    const studentRowHeight = 6;
    const maxStudentsPerPage = Math.floor(
      (pageHeight - margin * 2 - headerHeight - 20) / studentRowHeight
    );
  
    // Calculate all student stats
    const studentsWithStats = students.map(student => {
      let grandTotal = 0;
      const subjectResults = {};
      
      subjects.forEach(subject => {
        const firstCA = parseFloat(getScore(student, subject.id, 'firstca')) || 0;
        const secondCA = parseFloat(getScore(student, subject.id, 'secondca')) || 0;
        const exam = parseFloat(getScore(student, subject.id, 'exam')) || 0;
        const total = firstCA + secondCA + exam;
        
        subjectResults[subject.id] = { firstCA, secondCA, exam, total, grade: getGrade(total) };
        grandTotal += total;
      });
  
      const average = subjects.length > 0 ? grandTotal / subjects.length : 0;
      return {
        ...student,
        subjectResults,
        grandTotal,
        average: parseFloat(average.toFixed(2)),
        grandGrade: getGrade(average)
      };
    });
  
    // Calculate positions without sorting
    const averages = studentsWithStats.map(s => s.average);
    const sortedAverages = [...averages].sort((a, b) => b - a);
    studentsWithStats.forEach(student => {
      student.position = sortedAverages.indexOf(student.average) + 1;
    });
  
    // Calculate class statistics
    const classStats = {
      highestAverage: Math.max(...averages).toFixed(2),
      lowestAverage: Math.min(...averages).toFixed(2),
      classAverage: (averages.reduce((a, b) => a + b, 0) / averages.length).toFixed(2),
      topStudents: [...studentsWithStats]
        .sort((a, b) => b.average - a.average)
        .slice(0, 3),
      bottomStudents: [...studentsWithStats]
        .sort((a, b) => a.average - b.average)
        .slice(0, 3)
    };
  
    // Function to get ordinal position
    const getOrdinal = (n) => {
      if (isNaN(n)) return '';
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
  
    // Split subjects into pages (4 per page)
    const subjectPages = [];
    for (let i = 0; i < subjects.length; i += maxSubjectsPerPage) {
      subjectPages.push(subjects.slice(i, i + maxSubjectsPerPage));
    }
  
    // Split students into chunks
    const studentChunks = [];
    for (let i = 0; i < studentsWithStats.length; i += maxStudentsPerPage) {
      studentChunks.push(studentsWithStats.slice(i, i + maxStudentsPerPage));
    }
  
    let currentPage = 0;
  
    // Generate pages for subject data
    subjectPages.forEach((pageSubjects, subjectPageIndex) => {
      studentChunks.forEach((studentChunk, studentPageIndex) => {
        if (currentPage > 0) {
          doc.addPage('landscape');
        }
        currentPage++;
  
        // Header
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`${classInfo.name} BROAD SHEET`, margin, margin + 10);
        
        doc.setFontSize(12);
        doc.text(`Session: ${sessionName} | Term: ${termName}`, margin, margin + 16);
        doc.text(`Page ${currentPage}`, pageWidth - margin - 20, margin + 16);
  
        // Table headers
        const headers = [
          { 
            content: 'S/N', 
            styles: { 
              halign: 'center', 
              fillColor: [220, 220, 220],
              lineWidth: 0.1,
              lineColor: [0, 0, 0]
            } 
          },
          { 
            content: 'STUDENT NAME', 
            styles: { 
              fillColor: [220, 220, 220],
              lineWidth: 0.1,
              lineColor: [0, 0, 0]
            } 
          }
        ];
  
        // Subject headers (4 per page)
        pageSubjects.forEach(subject => {
          headers.push({
            content: subject.name,
            colSpan: 5,
            styles: {
              halign: 'center',
              fillColor: [220, 220, 220],
              lineWidth: 0.1,
              lineColor: [0, 0, 0]
            }
          });
        });
  
        // Add summary headers only on last subject page
        if (subjectPageIndex === subjectPages.length - 1) {
          headers.push(
            { content: 'Grand Total', colSpan: 1, styles: { halign: 'center', fillColor: [220, 220, 220] } },
            { content: 'Average', colSpan: 1, styles: { halign: 'center', fillColor: [220, 220, 220] } },
            { content: 'Grade', colSpan: 1, styles: { halign: 'center', fillColor: [220, 220, 220] } },
            { content: 'Position', colSpan: 1, styles: { halign: 'center', fillColor: [220, 220, 220] } }
          );
        }
  
        // Second header row
        const subHeaders = [
          { content: '', styles: { fillColor: [220, 220, 220] } },
          { content: '', styles: { fillColor: [220, 220, 220] } }
        ];
  
        pageSubjects.forEach(() => {
          subHeaders.push(
            { content: '1st CA', styles: { halign: 'center', fillColor: [240, 240, 240] } },
            { content: '2nd CA', styles: { halign: 'center', fillColor: [240, 240, 240] } },
            { content: 'Exam', styles: { halign: 'center', fillColor: [240, 240, 240] } },
            { content: 'Total', styles: { halign: 'center', fillColor: [240, 240, 240], fontStyle: 'bold' } },
            { content: 'Grade', styles: { halign: 'center', fillColor: [240, 240, 240], fontStyle: 'bold' } }
          );
        });
  
        // Add empty headers for summary columns if needed
        if (subjectPageIndex === subjectPages.length - 1) {
          subHeaders.push(
            { content: '', styles: { fillColor: [220, 220, 220] } },
            { content: '', styles: { fillColor: [220, 220, 220] } },
            { content: '', styles: { fillColor: [220, 220, 220] } },
            { content: '', styles: { fillColor: [220, 220, 220] } }
          );
        }
  
        // Prepare table data
        const body = studentChunk.map((student, idx) => {
          const globalIndex = studentPageIndex * maxStudentsPerPage + idx;
          const row = [
            { content: globalIndex + 1, styles: { halign: 'center' } },
            { content: getStudentName(student), styles: { } }
          ];
  
          // Subject scores
          pageSubjects.forEach(subject => {
            const result = student.subjectResults[subject.id];
            row.push(
              { content: result.firstCA, styles: { halign: 'center' } },
              { content: result.secondCA, styles: { halign: 'center' } },
              { content: result.exam, styles: { halign: 'center' } },
              { content: result.total, styles: { halign: 'center', fontStyle: 'bold' } },
              { content: result.grade, styles: { halign: 'center', fontStyle: 'bold' } }
            );
          });
  
          // Add summary columns only on last subject page
          if (subjectPageIndex === subjectPages.length - 1) {
            row.push(
              { content: student.grandTotal, styles: { halign: 'center', fontStyle: 'bold', textColor: (0, 0, 0)  } },
              { content: student.average, styles: { halign: 'center', fontStyle: 'bold', textColor: (0, 0, 0) } },
              { content: student.grandGrade, styles: { halign: 'center', fontStyle: 'bold', textColor: (0, 0, 0) } },
              { content: getOrdinal(student.position), styles: { halign: 'center', fontStyle: 'bold', textColor: (0, 0, 0) } }
            );
          }
  
          return row;
        });
  
        // Generate the table
        autoTable(doc, {
          startY: margin + headerHeight,
          head: [headers, subHeaders],
          body: body,
          styles: {
            fontSize: 8, textColor: (0, 0, 0) ,
            cellPadding: 2,
            lineColor: [0, 0, 0],
            lineWidth: 0.1
          },
          headStyles: {
            fillColor: [220, 220, 220],
            textColor: [0, 0, 0],
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          },
          margin: { left: margin }
        });
  
        // Footer
        const now = new Date();
        const options = { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        };
        const formattedDate = now.toLocaleDateString('en-US', options);
  
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(`Printed on ${formattedDate}`, pageWidth/2, pageHeight - 10);
        doc.text(`Generated by ${SCHOOL_ACRONYM} App`, margin, pageHeight - 10);
      });
    });
  
    // Add new page for class statistics
    doc.addPage('landscape');
    
    // Header for statistics page
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`${classInfo.name} CLASS STATISTICS`, margin, margin + 10);
    
    doc.setFontSize(12);
    doc.text(`Session: ${sessionName} | Term: ${termName}`, margin, margin + 16);
  
    // Calculate positions for side-by-side tables
    const statsPageWidth = doc.internal.pageSize.getWidth();
    const tableWidth = (statsPageWidth - margin * 2 - 20) / 3;
    
    // Left table - Class Statistics
    autoTable(doc, {
      startY: margin + headerHeight,
      head: [['Class Statistics', 'Value']],
      body: [
        ['Highest Avg', classStats.highestAverage],
        ['Lowest Avg', classStats.lowestAverage],
        ['Class Avg', classStats.classAverage]
      ],
      styles: { 
        fontSize: 10, 
        textColor: [0, 0, 0],
        cellPadding: 4,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        halign: 'center'
      },
      headStyles: { 
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: tableWidth * 0.6 },
        1: { halign: 'center', cellWidth: tableWidth * 0.4 }
      },
      margin: { left: margin },
      tableWidth: tableWidth
    });
  
    // Middle table - Top 3 Students
    autoTable(doc, {
      startY: margin + headerHeight,
      head: [['Top 3 Students', 'Avg', 'Pos']],
      body: classStats.topStudents.map(student => [
        getStudentName(student),
        student.average,
        getOrdinal(student.position)
      ]),
      styles: { 
        fontSize: 9, 
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { cellWidth: tableWidth * 0.6, halign: 'left' },
        1: { cellWidth: tableWidth * 0.2, halign: 'center' },
        2: { cellWidth: tableWidth * 0.2, halign: 'center' }
      },
      headStyles: { 
        fillColor: [220, 220, 220] 
      },
      margin: { left: margin + tableWidth + 10 },
      tableWidth: tableWidth
    });
    
    // Right table - Bottom 3 Students
    autoTable(doc, {
      startY: margin + headerHeight,
      head: [['Bottom 3 Students', 'Avg', 'Pos']],
      body: classStats.bottomStudents.map(student => [
        getStudentName(student),
        student.average,
        getOrdinal(student.position)
      ]),
      styles: { 
        fontSize: 9, 
        textColor: [0, 0, 0], 
      },
      columnStyles: {
        0: { cellWidth: tableWidth * 0.6, halign: 'left' },
        1: { cellWidth: tableWidth * 0.2, halign: 'center' },
        2: { cellWidth: tableWidth * 0.2, halign: 'center' }
      },
      headStyles: { 
        fillColor: [220, 220, 220] 
      },
      margin: { left: margin + (tableWidth + 10) * 2 },
      tableWidth: tableWidth
    });
  
    // Footer for statistics page
    const now = new Date();
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    const formattedDate = now.toLocaleDateString('en-US', options);
  
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(`Printed on ${formattedDate}`, statsPageWidth/2, pageHeight - 10);
    doc.text(`Generated by ${SCHOOL_ACRONYM} App`, margin, pageHeight - 10);
  
    doc.save(`${classInfo.name}_BroadSheet_${sessionName}_${termName}.pdf`);
  };

  // First, ensure you have these helper functions defined
const getOrdinal = (n) => {
  if (isNaN(n)) return '';
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

  const generateStudentResults = () => {

    if (!classInfo || !students || students.length === 0 || !subjects || subjects.length === 0) {
      console.error('Missing required data');
      return;
    }

    // First calculate subject statistics for the entire class
    const subjectStats = {};
    subjects.forEach(subject => {
      const subjectScores = students.map(student => {
        const firstCA = parseFloat(getScore(student, subject.id, 'firstca')) || 0;
        const secondCA = parseFloat(getScore(student, subject.id, 'secondca')) || 0;
        const exam = parseFloat(getScore(student, subject.id, 'exam')) || 0;
        return firstCA + secondCA + exam;
      });
      
      subjectStats[subject.id] = {
        highest: Math.max(...subjectScores),
        lowest: Math.min(...subjectScores),
        average: parseFloat((subjectScores.reduce((a, b) => a + b, 0) / subjectScores.length).toFixed(0))
      };
    });

    // Calculate student stats
    const studentsWithStats = students.map(student => {
      let grandTotal = 0;
      const subjectResults = {};
      
      subjects.forEach(subject => {
        const firstCA = parseFloat(getScore(student, subject.id, 'firstca')) || 0;
        const secondCA = parseFloat(getScore(student, subject.id, 'secondca')) || 0;
        const exam = parseFloat(getScore(student, subject.id, 'exam')) || 0;
        const total = firstCA + secondCA + exam;
        
        subjectResults[subject.id] = { 
          firstCA, 
          secondCA, 
          exam, 
          total, 
          grade: getGrade(total) 
        };
        grandTotal += total;
      });

      const average = subjects.length > 0 ? grandTotal / subjects.length : 0;
      return {
        ...student,
        subjectResults,
        grandTotal,
        average: parseFloat(average.toFixed(2)),
        grandGrade: getGrade(average),
        comment: getComment(average),
        position: 0 // Will be calculated
      };
    });

    // Calculate positions and class statistics
    const averages = studentsWithStats.map(s => s.average);
    const sortedAverages = [...averages].sort((a, b) => b - a);
    
    // Calculate class statistics
    const highestAverage = Math.max(...averages);
    const lowestAverage = Math.min(...averages);
    const classAverage = averages.reduce((sum, avg) => sum + avg, 0) / averages.length;
    
    studentsWithStats.forEach(student => {
      student.position = sortedAverages.indexOf(student.average) + 1;
    });

    // Create a single PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm'
    });

    // Generate each student's page
    studentsWithStats.forEach((student, index) => {
      if (index > 0) {
        doc.addPage();
      }

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(... RESULT_COLOR_1);
      doc.text(`${SCHOOL_NAME}`.toUpperCase(), pageWidth/2, 20, { align: 'center' });

      // === Add Logo ===
      const logoX = 14;
      const logoY = 13;
      const logoWidth = 22;
      const logoHeight = 22;

      doc.addImage(Logo, 'PNG', logoX, logoY, logoWidth, logoHeight);
      
      // School details
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`${SCHOOL_ADDRESS}`, pageWidth/2, 27, { align: 'center' });
      doc.text(`${SCHOOL_PHONE} | ${SCHOOL_EMAIL}`, pageWidth/2, 32, { align: 'center' });
      
      // Divider line
      doc.setDrawColor(0, 0, 128);
      doc.setLineWidth(0.5);
      doc.line(margin, 37, pageWidth - margin, 37);

      // Report title
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 128);
      doc.text('STUDENT REPORT SHEET', pageWidth/2, 45, { align: 'center' });
      
      // Student details
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Name: ${getStudentName(student)}`, margin, 55);
      doc.text(`Admission No: ${student.admissionNumber || 'N/A'}`, margin, 62);
      doc.text(`Class: ${classInfo.name}`, pageWidth/2, 55);
      doc.text(`Session: ${sessionName}`, pageWidth/2, 62);
      doc.text(`Term: ${termName}`, pageWidth - margin, 55, { align: 'right' });
      doc.text(`Position: ${getOrdinal(student.position)}`, pageWidth - margin, 62, { align: 'right' });

      // Performance summary - replaced repetitive stats with class statistics
      doc.setFillColor(...RESULT_COLOR_3);
      doc.roundedRect(margin, 65, pageWidth - 2*margin, 15, 3, 3, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      
      doc.text(`Highest Avg: ${highestAverage.toFixed(2)}`, margin + 10, 71);
      doc.text(`Class Avg: ${classAverage.toFixed(2)}`, pageWidth/2 - 14, 71);
      doc.text(`Lowest Avg: ${lowestAverage.toFixed(2)}`, pageWidth - margin - 50, 71);

      // Student's individual performance
      doc.text(`Your Average: ${student.average}`, margin + 10, 77);
      doc.text(`Your Grade: ${student.grandGrade}`, pageWidth/2 - 14, 77);
      doc.text(`Your Position: ${getOrdinal(student.position)}`, pageWidth - margin - 50, 77);

      // === Calculate dynamic row height to fill fixed table height ===
      const tableStartY = 84;
      const tableEndY = pageHeight - 95;
      const availableTableHeight = tableEndY - tableStartY;
      const numberOfRows = subjects.length;
      const dynamicRowHeight = availableTableHeight / (numberOfRows + 1); 

      const psychomotorSkills = getPsychomotorSkills(student);
      const psychomotorData = [
        ['Handwriting', psychomotorSkills.handwriting || 'A'],
        ['Fluency', psychomotorSkills.fluency || 'A'],
        ['Games/Sports', psychomotorSkills.games || 'A'],
        ['Punctuality', psychomotorSkills.punctuality || 'A'],
        ['Neatness', psychomotorSkills.neatness || 'A'],
        ['Politeness', psychomotorSkills.politeness || 'A'],
        ['Cooperation', psychomotorSkills.cooperation || 'A'],
        ['Leadership', psychomotorSkills.leadership || 'A'],
        ['Attentiveness', psychomotorSkills.attentiveness || 'A'],
        ['Honesty', psychomotorSkills.honesty || 'A'],
      ];

    const availableWidth = pageWidth - (margin * 2);
    
    autoTable(doc, {
      startY: tableStartY,
      head: [['S/N', 'Subject', '1st CA', '2nd CA', 'Exam', 'Total', 'Grade', 
      { 
        content: 'Highest', 
        styles: { 
          fontSize: 6,
        } 
      },
      { 
        content: 'Lowest', 
        styles: { 
          fontSize: 6,
        } 
      },
      { 
        content: 'Avg', 
        styles: { 
          fontSize: 6,
        } 
      },
    ]],
      body: subjects.map((subject, index) => {
        const result = student.subjectResults[subject.id];
        return [
          index + 1,
          subject.name,
          result.firstCA,
          result.secondCA,
          result.exam,
          result.total,
          result.grade,
          subjectStats[subject.id].highest,
          subjectStats[subject.id].lowest,
          subjectStats[subject.id].average
        ];
      }),
      headStyles: {
        fillColor: RESULT_COLOR_2,
        textColor: [255, 255, 255],
        fontSize: 9,
        halign: 'center',
        valign: 'middle'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: [0, 0, 0],
        minCellHeight: dynamicRowHeight,
        valign: 'middle'
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240]
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: availableWidth * 0.05 },  // S/N
        1: { halign: 'left', cellWidth: availableWidth * 0.25 },    // Subject
        2: { halign: 'center', cellWidth: availableWidth * 0.1 },  // 1st CA
        3: { halign: 'center', cellWidth: availableWidth * 0.1 },  // 2nd CA
        4: { halign: 'center', cellWidth: availableWidth * 0.1 },  // Exam
        5: { halign: 'center', cellWidth: availableWidth * 0.1, fontStyle: 'bold' },  // Total
        6: { halign: 'center', cellWidth: availableWidth * 0.09, fontStyle: 'bold' },  // Grade
        7: { halign: 'center', cellWidth: availableWidth * 0.07, fontSize: 7},   // Highest
        8: { halign: 'center', cellWidth: availableWidth * 0.07, fontSize: 7 },   // Lowest
        9: { halign: 'center', cellWidth: availableWidth * 0.07, fontSize: 7 }    // Avg
      },
      margin: { left: margin, right: margin },
      styles: {
        lineWidth: 0.1,
        lineColor: [255, 255, 255],
        // cellPadding: 2
      },
      tableWidth: 'wrap' // This will make the table use available width
    });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 4,
        head: [
          [
            { 
              content: 'PSYCHOMOTOR SKILLS ASSESSMENT', 
              colSpan: 5, 
              styles: { 
                halign: 'center', 
                fillColor: RESULT_COLOR_2,
                textColor: [255, 255, 255],
                fontSize: 10
              } 
            }
          ],
          [
            // Group 1 sub-headers
            { content: 'Skill', styles: { halign: 'left' } },
            { content: 'Rating', styles: { halign: 'center' } },
            // Group 2 sub-headers
            { content: 'Skill', styles: { halign: 'left' } },
            { content: 'Rating', styles: { halign: 'center' } },
            // Grading Key header
            { content: 'Grading Key', styles: { halign: 'center' } }
          ]
        ],
        body: [
          // Row 1
          [
            'Handwriting',
            psychomotorSkills.handwriting || 'A',
            'Politeness',
            psychomotorSkills.politeness || 'A',
            'A - Excellent',                        
          ],
          // Row 2
          [
            'Verbal Fluency',
            psychomotorSkills.fluency || 'A',
            'Cooperation',
            psychomotorSkills.cooperation || 'A',
            'B - Very Good',            
          ],
          // Row 3
          [
            'Games & Sports',
            psychomotorSkills.games|| 'A',
            'Leadership',
            psychomotorSkills.leadership || 'A',
            'C - Satisfactory',
          ],
          // Row 4
          [
            'Punctuality',
            psychomotorSkills.punctuality || 'A',
            'Attentiveness',
            psychomotorSkills.attentiveness || 'A',
            'D - Fairly Good',
          ],
          // Row 5
          [
            'Neatness',
            psychomotorSkills.neatness || 'A',
            'Honesty',
            psychomotorSkills.honesty || 'A',
            'E - Needs Improvement',
          ]
        ],
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontSize: 8,
          lineWidth: 0.3
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [0, 0, 0],
          lineWidth: 0.2,
          lineColor: [200, 200, 200],
          cellPadding: {
            top: 1,
            right: 1,
            bottom: 1,
            left: 2
          }
        },
        columnStyles: {
          0: { cellWidth: '25%', halign: 'left' },  // Skill 1
          1: { cellWidth: '15%', halign: 'center' }, // Rating 1
          2: { cellWidth: '25%', halign: 'left' },  // Skill 2
          3: { cellWidth: '15%', halign: 'center' }, // Rating 2
          4: { cellWidth: '20%', halign: 'center' }    // Grading Key
        },
        margin: { left: margin, right: margin },
        styles: {
          lineWidth: 0.2,
          lineColor: [200, 200, 200]
        },
        tableWidth: 'auto'
      });
      
      // Comment - Bold only the label
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Comment: ', margin, pageHeight - 31);
      doc.setFont('helvetica', 'normal');
      doc.text(student.comment, margin + 20, pageHeight - 31);

      // Form Master - Bold only the label
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Class Teacher: ', margin, pageHeight - 22);
      doc.setFont('helvetica', 'normal');
      doc.text(formMasterName || '________________________', margin + 28, pageHeight - 22);

      // Date - Bold only the label
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      const dateTextWidth = doc.getTextWidth('Date: ');
      doc.text("Principal's Sign/Date: ", pageWidth - margin - dateTextWidth - 77, pageHeight - 22);
      doc.setFont('helvetica', 'normal');
      doc.text('______________________', pageWidth - margin, pageHeight - 22, { align: 'right' });

      const now = new Date();
      const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      };
      const formattedDate = now.toLocaleDateString('en-US', options);

      // Footer with date/time      
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text(`Printed on ${formattedDate}`, pageWidth/2, pageHeight - 7, { align: 'center' });
    });

    // Save the combined PDF
    doc.save(`${classInfo.name}_${sessionName}_${termName}_Results.pdf`);
  };

  return (
    <>
      <div className='scroll-fixed' style={{minHeight: '100vh'}}>
      <div className="top-elements row align-center space-between">
        <button onClick={() => navigate(-1)} className="back-link back-link1">
          <i className="fa-solid fa-arrow-left size3"></i>
        </button>
        <div className="column align-center justify-center">
          <img src={Logo} className="middle-logo" alt="logo" />
        </div>
        <Link to="#" className="home-link" onClick={() => window.location.reload()}>
          <i className="fa-solid fa-rotate-right size4"></i>
        </Link>
      </div>
      <div className='scroll-fixed'>
        <div className='breadcrumb breadcrumb1'>
          <button onClick={() => navigate(-4)} className="back-link back-link1 back-link2">
            Section &nbsp;&nbsp;&nbsp;<i className="fa-solid fa-angle-right font-gold size4"></i>
          </button>
          {(userData?.status === 'Admin' || userData?.status === 'Cashier') && ( 
            <>
              <button onClick={() => navigate(-3)} className="back-link back-link1 back-link2">
                Session &nbsp;&nbsp;&nbsp;<i className="fa-solid fa-angle-right font-gold size4"></i>
              </button>
              <button onClick={() => navigate(-2)} className="back-link back-link1 back-link2">
                Term &nbsp;&nbsp;&nbsp;<i className="fa-solid fa-angle-right font-gold size4"></i>
              </button>
              <button onClick={() => navigate(-1)} className="back-link back-link1 back-link2">
                Class &nbsp;&nbsp;&nbsp;<i className="fa-solid fa-angle-right font-gold size4"></i>
              </button>
              <button className="back-link back-link1 back-link2">
                Result
              </button>
            </>
          )}
        </div>

        <p className='text-center size4 bold class-results-heading'>{classInfo?.name} Results <span className='size5'>({sessionName} - {termName} Term)</span></p>
        {(userData?.status === 'Admin' || userData?.status === 'Cashier') && (
          <div className='align-center space-around class-results-buttons'>
            <button className='list-btn bold' onClick={generatePDF} >
              Score Sheets
            </button>
            <button className='list-btn bold' onClick={generateBroadSheet}>
              Broad Sheet
            </button>
            <button 
              className='list-btn bold' 
              onClick={generateStudentResults}
            >
              Student Results
            </button>
          </div>
        )}
      </div>

    <div className='scroll-fixed scroll-fixed1'>
      <div className='scrollable'>
      {loading ? (
        <p className='male-female-stat' style={{marginLeft: '18px'}}>Loading results ...</p>
      ) : (
        <>        
          <div style={{ padding: '0', overflowX: 'auto', maxHeight: '80vh', marginTop: '0px' }}>         
          {(() => {
            const studentsWithStats = students.map(student => {
              let grandTotal = 0;
              const subjectResults = {};
              
              subjects.forEach(subject => {
                const firstCA = parseFloat(getScore(student, subject.id, 'firstca')) || 0;
                const secondCA = parseFloat(getScore(student, subject.id, 'secondca')) || 0;
                const exam = parseFloat(getScore(student, subject.id, 'exam')) || 0;
                const total = firstCA + secondCA + exam;
                
                subjectResults[subject.id] = { firstCA, secondCA, exam, total, grade: getGrade(total) };
                grandTotal += total;
              });
            
              const average = subjects.length > 0 ? grandTotal / subjects.length : 0;
              return {
                ...student,
                subjectResults,
                grandTotal,
                average: parseFloat(average.toFixed(2)),
                grandGrade: getGrade(average)
              };
            });
            
            // Calculate positions without sorting the original array
            const averages = studentsWithStats.map(student => student.average);
            const sortedAverages = [...averages].sort((a, b) => b - a);
            
            studentsWithStats.forEach(student => {
              // Find position in sorted averages array
              student.position = sortedAverages.indexOf(student.average) + 1;
              
              // Handle ties - students with same average get same position
              const sameAverageStudents = studentsWithStats.filter(s => 
                s.average === student.average && s.position < student.position
              );
              if (sameAverageStudents.length > 0) {
                student.position = sameAverageStudents[0].position;
              }
            });
            
            // Calculate class statistics (create sorted version just for this)
            const studentsSortedForStats = [...studentsWithStats].sort((a, b) => b.average - a.average);
            const classStats = {
              highestAverage: Math.max(...averages).toFixed(2),
              lowestAverage: Math.min(...averages).toFixed(2),
              classAverage: (averages.reduce((a, b) => a + b, 0) / averages.length).toFixed(2),
              topStudents: studentsSortedForStats.slice(0, 3),
              bottomStudents: studentsSortedForStats.slice(-3).reverse()
            };

            // Function to get ordinal position (1st, 2nd, etc.)
            const getOrdinal = (n) => {
              const s = ["th", "st", "nd", "rd"];
              const v = n % 100;
              return n + (s[(v - 20) % 10] || s[v] || s[0]);
            };

            return (
              <>
                {/* Class Statistics Section */}
                <div style={{ 
                  marginBottom: '10px', 
                  padding: '20px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '5px',
                  border: '1px solid #ddd'
                }}>
                  <p className='bold male-female-stat'>CLASS STATISTICS</p>
                  
                  <div>
                    <p className='male-female-stat'><strong>Highest Average:</strong> {classStats.highestAverage}</p>
                    <p className='male-female-stat'><strong>Lowest Average:</strong> {classStats.lowestAverage}</p>
                    <p className='male-female-stat'><strong>Class Average:</strong> {classStats.classAverage}</p>
                  </div>
                  
                  <div>
                    <div>
                      <p className='bold male-female-stat'style={{ marginBottom: '10px' }}>TOP 3 STUDENTS</p>
                      {classStats.topStudents.map(student => (
                        <div className='male-female-stat' key={student.id} style={{ marginBottom: '8px' }}>
                          <strong>{getOrdinal(student.position)}:</strong> {getStudentName(student)} ({student.average})
                        </div>
                      ))}
                    </div>
                    
                    <div>
                      <p className='bold male-female-stat' style={{ marginBottom: '10px' }}>BOTTOM 3 STUDENTS</p>
                      {classStats.bottomStudents.map(student => (
                        <div className='male-female-stat' key={student.id} style={{ marginBottom: '8px' }}>
                          <strong>{getOrdinal(student.position)}:</strong> {getStudentName(student)} ({student.average})
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <table
                  className='male-female-stat'
                  border="1"
                  cellPadding="8"
                  style={{
                    borderCollapse: 'collapse',
                    minWidth: '100%',
                    marginBottom: '30px',
                    textAlign: 'center',
                  }}
                >
                  <thead>
                    <tr>
                      <th rowSpan="2">S/N</th>
                      <th rowSpan="2" style={{ minWidth: '200px' }}>Student</th>
                      {subjects.map(subject => (
                        <th key={subject.id} colSpan="5">
                          {subject.name}<br />
                          <small style={{ fontWeight: 'normal' }}>{subject.assignedTeachers || 'No teacher'}</small>
                        </th>
                      ))}
                      <th colSpan="10">
                        Psychomotor Skills<br />
                        <small style={{ fontWeight: 'normal' }}>{formMasterName || 'No teacher'}</small>                      
                      </th>
                      <th colSpan="4">Summary</th>
                    </tr>
                    <tr>
                      {subjects.map(subject => (
                        <React.Fragment key={subject.id}>
                          <th>1st CA</th>
                          <th>2nd CA</th>
                          <th>Exam</th>
                          <th>Total</th>
                          <th>Grade</th>
                        </React.Fragment>
                      ))}
                      <th>H/W</th>
                      <th>Fluent</th>
                      <th>Games</th>
                      <th>Punct</th>
                      <th>Neat</th>
                      <th>Polite</th>
                      <th>Coperate</th>
                      <th>Lead</th>
                      <th>Attention</th>
                      <th>Honest</th>
                      <th>Total</th>
                      <th>Average</th>
                      <th>Grade</th>
                      <th>Position</th>
                    </tr>
                  </thead>
                  <tbody className='result-table'>
                    {studentsWithStats.map((student, index) => {
                      const psychomotor = getPsychomotorSkills(student);
                      return (
                        <tr key={student.id}>
                          <td>{index + 1}</td>
                          <td style={{ minWidth: '200px', textAlign: 'left' }}>{getStudentName(student)}</td>
                          {subjects.map(subject => {
                            const result = student.subjectResults[subject.id];
                            return (
                              <React.Fragment key={`${student.id}-${subject.id}`}>
                                <td>{result.firstCA}</td>
                                <td>{result.secondCA}</td>
                                <td>{result.exam}</td>
                                <td style={{ fontWeight: 'bold' }}>{result.total}</td>
                                <td style={{ fontWeight: 'bold', color: result.grade === 'F' ? 'red' : 'inherit' }}>{result.grade}</td>
                              </React.Fragment>
                            );
                          })}
                          <td>{psychomotor.handwriting || 'A'}</td>
                          <td>{psychomotor.fluency || 'A'}</td>
                          <td>{psychomotor.games || 'A'}</td>
                          <td>{psychomotor.punctuality || 'A'}</td>
                          <td>{psychomotor.neatness || 'A'}</td>
                          <td>{psychomotor.politeness || 'A'}</td>
                          <td>{psychomotor.cooperation || 'A'}</td>
                          <td>{psychomotor.leadership || 'A'}</td>
                          <td>{psychomotor.attentiveness || 'A'}</td>
                          <td>{psychomotor.honesty || 'A'}</td>
                          <td style={{ fontWeight: 'bold' }}>{student.grandTotal}</td>
                          <td style={{ fontWeight: 'bold' }}>{student.average}</td>
                          <td style={{ fontWeight: 'bold', color: student.grade === 'F' ? 'red' : 'inherit' }}>{student.grandGrade}</td>
                          <td style={{ fontWeight: 'bold' }}>{getOrdinal(student.position)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            );
          })()}
        </div>
        </>
      )}
      </div>
      </div>
      </div>
    </>
  );
};

export default ClassResults;
